import { useEffect, useState } from 'react';
import { ApiError, adminApi, type AdminVersionSummary } from '../../lib/api';
import { relativeTime } from '../../lib/format';
import ConfirmModal from '../ConfirmModal';
import SheetDialog from '../SheetDialog';
import { staticVersionPreview } from '../../lib/versionPreview';

const REASON_LABELS: Record<string, string> = {
  manual: 'Manual save',
  publish: 'Published',
  pre_restore: 'Before a restore',
  signed: 'Signed',
};

export default function VersionsPanel({
  proposalId,
  open,
  onClose,
  onRestored,
  flushPending,
}: {
  proposalId: string;
  open: boolean;
  onClose: () => void;
  onRestored: () => void;
  /** Flushes pending autosave edits and reports whether it's safe to
   *  proceed; restoring on top of an unsaved (or unsaveable) edit would
   *  either lose it or race the restore, so this runs first. */
  flushPending: () => Promise<boolean>;
}) {
  const [versions, setVersions] = useState<AdminVersionSummary[] | null>(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<AdminVersionSummary | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setVersions(null);
    setConfirming(false);
    setRestoreError(null);
    setError('');
    setSelected(null);
    adminApi
      .listVersions(proposalId)
      .then((result) => { if (!cancelled) setVersions(result.versions); })
      .catch((caught) => { if (!cancelled) setError(caught instanceof ApiError ? caught.message : 'Could not load versions.'); });
    return () => { cancelled = true; };
  }, [open, proposalId]);

  useEffect(() => {
    setPreview(null);
    setSnapshot('');
    setPreviewError('');
    if (!open || !selected) return;
    let cancelled = false;
    Promise.all([adminApi.getVersion(proposalId, selected.id), import('../../../worker/render')])
      .then(([version, { renderProposal }]) => {
        // The production renderer, isolated without scripts, forms or same-origin access.
        const { html } = renderProposal(version.doc, { slug: 'version-preview', docVersion: version.doc_version });
        if (!cancelled) { setPreview(staticVersionPreview(html)); setSnapshot(JSON.stringify(version.doc, null, 2)); }
      }).catch(() => { if (!cancelled) setPreviewError('Could not load this snapshot. Select it again to retry.'); });
    return () => { cancelled = true; };
  }, [open, selected, proposalId]);

  async function restore() {
    if (!selected) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      if (!(await flushPending())) {
        setRestoreError('Your latest edits could not be saved. Fix that, then try again.');
        return;
      }
      await adminApi.restoreVersion(proposalId, selected.id);
      setConfirming(false);
      onRestored();
      onClose();
    } catch (caught) {
      setRestoreError(caught instanceof ApiError ? caught.message : 'Could not restore this version.');
    } finally {
      setRestoring(false);
    }
  }

  if (!open) return null;

  return (
    <SheetDialog open={open} title="Version history" onClose={onClose} busy={restoring} wide>
      <div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && <p className="text-sm text-error">{error}</p>}
          {!error && versions === null && <p className="text-sm text-ink-faint">Loading…</p>}
          {!error && versions && versions.length === 0 && <p className="text-sm text-ink-faint">No saved versions yet.</p>}

          <div className="space-y-2">
            {versions?.map((version) => (
              <button
                key={version.id}
                type="button"
                onClick={() => setSelected({ ...version })}
                className={
                  'w-full rounded-xl border px-4 py-3 text-left transition ' +
                  (selected?.id === version.id ? 'border-accent bg-accent-tint' : 'border-line hover:border-ink-faint')
                }
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-ink-faint">v{version.doc_version}</span>
                  <span className="text-xs text-ink-faint">{relativeTime(version.created_at)}</span>
                </div>
                <div className="mt-1 text-sm text-ink">{REASON_LABELS[version.reason] ?? version.reason}</div>
              </button>
            ))}
          </div>
        </div>

        {selected && <div className="px-6 pb-5">
          <h3 className="font-display text-xl">Version {selected.doc_version} preview</h3>
          {previewError ? <p role="alert" className="text-error">{previewError}</p> : preview
            ? <iframe className="version-preview" title={`Version ${selected.doc_version} preview`} srcDoc={preview} sandbox="" />
            : <p>Loading snapshot…</p>}
          {snapshot && <details className="mt-4"><summary>All saved fields</summary><pre className="mt-3 whitespace-pre-wrap break-words font-mono text-xs">{snapshot}</pre></details>}
        </div>}
        {selected && (
          <div className="border-t border-line px-6 py-5">
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!preview}
              className="w-full rounded-full bg-accent px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-accent-deep"
            >
              Restore v{selected.doc_version}
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirming}
        title={`Restore version ${selected?.doc_version ?? ''}`}
        message="This replaces the current draft with this version's content. The current state is saved as its own version first, so nothing is lost."
        confirmLabel="Restore this version"
        busy={restoring}
        errorMessage={restoreError}
        onConfirm={restore}
        onCancel={() => (restoring ? null : setConfirming(false))}
      />
    </SheetDialog>
  );
}
